import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Info, LineChart as LineChartIcon, Loader2, TrendingDown, TrendingUp } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "../../lib/api";
import { formatMinor } from "../../lib/finance";
import { SEVERITY_LABELS, STATE_LABELS, TREND_KPIS, cockpitError, deltaOf, formatScalar, monthLabel, moneyLines, partText, trendSeries, whenText } from "../../lib/cockpit";
import { Donut, HBars, RAMP, STATUS, Spark } from "../charts/Charts";
import { Card } from "../ui/card";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../ui/responsive-popover";

// The « i » of a tile: what the figure means, who owns the data, where it comes from, how fresh, which caution (CDC V2 §12.1).
function Definition({ kpi, version }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label={`Définition de « ${kpi.label} »`} className="shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`kpi-info-${kpi.id}`}>
          <Info size={14} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-2 text-sm" align="end">
        <p className="font-semibold text-slate-900">{kpi.label}</p>
        <p className="text-slate-700">{kpi.definition}</p>
        <dl className="space-y-1 text-xs text-slate-600">
          <div><dt className="inline font-medium">Propriétaire de la donnée : </dt><dd className="inline">{kpi.owner}</dd></div>
          <div><dt className="inline font-medium">Source : </dt><dd className="inline">{kpi.source}</dd></div>
          <div><dt className="inline font-medium">Mise à jour : </dt><dd className="inline">{kpi.refresh}</dd></div>
          <div><dt className="inline font-medium">Dictionnaire : </dt><dd className="inline">version {version}</dd></div>
        </dl>
        {kpi.caution && <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900" data-testid={`kpi-caution-${kpi.id}`}>{kpi.caution}</p>}
      </PopoverContent>
    </Popover>
  );
}

// Colour of a state-like key (health, severity, priority...): status colours only for states, the brand ramp for everything else.
const STATE_COLORS = { critical: STATUS.critical, blocked: STATUS.critical, at_risk: STATUS.critical, breached: STATUS.critical, high: STATUS.serious, major: STATUS.serious,
  attention: STATUS.warning, medium: STATUS.warning, warning: STATUS.warning, low: STATUS.neutral, minor: STATUS.neutral, unknown: STATUS.neutral,
  on_track: STATUS.good, ok: STATUS.good, good: STATUS.good };
const partColor = (key, index) => STATE_COLORS[key] || RAMP[Math.min(index, RAMP.length - 1)];
// A breakdown of plain counts reads better as a chart than as a list; anything with a text, an amount or a rate keeps the list.
const chartable = (entry) => entry.parts && entry.parts.length >= 2 && entry.parts.every((p) => typeof p.value === "number" && !p.money && !p.text) && entry.parts.some((p) => p.value > 0);

function useTrend(kpiId, enabled) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    api.get("/cockpit/trends", { params: { kpi: kpiId, months: 12 } }).then(({ data: d }) => live && setData(d)).catch(() => live && setData(null));
    return () => { live = false; };
  }, [kpiId, enabled]);
  return data;
}

function TrendSpark({ kpi, trend }) {
  if (!trend) return null;
  const series = trendSeries(trend.points, trend.unit)[0];
  if (!series) return null;
  const money = series.unit === "money";
  return <Spark testid={`kpi-${kpi.id}-spark`} points={series.data.map((d) => ({ label: monthLabel(d.month), value: d.value }))} height={48}
    format={(v) => (money ? formatMinor(v, series.currency) : v)} />;
}

function Value({ entry, kpi }) {
  if (chartable(entry)) {
    const rows = entry.parts.map((p, i) => ({ id: p.key, label: p.label, value: p.value, color: partColor(p.key, i), keep: false }));
    return (
      <div data-testid={`kpi-${entry.id}-value`}>
        {rows.length <= 5 ? <Donut rows={rows} size={96} testid={`kpi-${entry.id}-chart`} /> : <HBars rows={rows} testid={`kpi-${entry.id}-chart`} />}
      </div>
    );
  }
  if (entry.state === "no_data") return <p className="text-sm text-slate-500" data-testid={`kpi-${entry.id}-no-data`}>Aucune donnée sur cette période</p>;
  if (entry.money) {
    return (
      <div className="space-y-0.5" data-testid={`kpi-${entry.id}-value`}>
        {moneyLines(entry.money).map((line) => <p key={line.currency} className="font-display text-xl font-bold text-slate-900 tabular-nums">{line.text}</p>)}
      </div>
    );
  }
  if (entry.parts) {
    return (
      <ul className="space-y-0.5" data-testid={`kpi-${entry.id}-value`}>
        {entry.parts.map((p) => (
          <li key={p.key} className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-slate-600">{p.label}</span>
            <span className="font-semibold text-slate-900 tabular-nums text-right">{partText(p, kpi.unit)}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="font-display text-2xl font-bold text-slate-900 tabular-nums" data-testid={`kpi-${entry.id}-value`}>{formatScalar(entry.value, kpi.unit)}</p>;
}

function Delta({ entry, kpi, compact = false }) {
  const delta = deltaOf(entry, kpi.unit);
  if (!delta) return null;
  const Icon = delta.tone === "down" ? TrendingDown : TrendingUp;
  return (
    <p className={`inline-flex items-center gap-1 text-xs ${delta.tone === "flat" ? "text-slate-500" : "text-slate-600"}`} data-testid={`kpi-${entry.id}-delta`}>
      {delta.tone !== "flat" && <Icon size={12} aria-hidden="true" />}{delta.text}{!compact && <span className="text-slate-400"> vs période précédente</span>}
    </p>
  );
}

// One tile = one indicator of the dictionary: value, when it holds, change, definition, and the way to the items behind it.
export function Tile({ entry, kpi, period, version, isMobile }) {
  const [trend, setTrend] = useState(false);
  const trendData = useTrend(entry.id, !!kpi && TREND_KPIS.includes(entry.id) && entry.state !== "unavailable");
  if (!kpi) return null;
  const unavailable = entry.state === "unavailable";
  return (
    <div className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-slate-200 bg-white p-3" data-testid={`kpi-${entry.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800">{kpi.label}</p>
          <p className="text-2xs text-slate-500">{whenText(kpi, period)}</p>
        </div>
        <Definition kpi={kpi} version={version} />
      </div>
      {unavailable ? (
        <p className="text-sm text-slate-500" data-testid={`kpi-${entry.id}-unavailable`}>{entry.reason}</p>
      ) : (
        <>
          <Value entry={entry} kpi={kpi} />
          <Delta entry={entry} kpi={kpi} />
          <TrendSpark kpi={kpi} trend={trendData} />
          {entry.state === "partial" && <p className="inline-flex items-center gap-1 text-xs text-amber-800" data-testid={`kpi-${entry.id}-partial`}><AlertTriangle size={12} aria-hidden="true" />{STATE_LABELS.partial} : la source atteint sa limite.</p>}
          {entry.note && <p className="text-xs text-slate-500">{entry.note}</p>}
        </>
      )}
      {!unavailable && (kpi.to || (TREND_KPIS.includes(entry.id) && !isMobile)) && (
        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-xs">
          {kpi.to && <Link to={kpi.to} className="inline-flex items-center gap-1 text-jamora hover:underline" data-testid={`kpi-${entry.id}-link`}>Voir<ArrowRight size={12} aria-hidden="true" /></Link>}
          {TREND_KPIS.includes(entry.id) && !isMobile && (
            <button type="button" onClick={() => setTrend((v) => !v)} aria-expanded={trend} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid={`kpi-${entry.id}-trend-toggle`}>
              <LineChartIcon size={12} aria-hidden="true" />{trend ? "Masquer la tendance" : "Tendance sur 12 mois"}
            </button>
          )}
        </div>
      )}
      {trend && <TrendBlock kpi={kpi} />}
    </div>
  );
}

// A flow over 12 calendar months (billed, collected, leads, tickets) — never a state. A table with the same numbers sits under the chart.
function TrendBlock({ kpi }) {
  const [state, setState] = useState({ phase: "loading" });
  useEffect(() => {
    let live = true;
    api.get("/cockpit/trends", { params: { kpi: kpi.id, months: 12 } })
      .then(({ data }) => live && setState({ phase: "ready", data }))
      .catch((e) => live && setState({ phase: "error", message: cockpitError(e, "Tendance indisponible.") }));
    return () => { live = false; };
  }, [kpi.id]);
  if (state.phase === "loading") return <p className="inline-flex items-center gap-1.5 text-xs text-slate-500"><Loader2 size={12} className="animate-spin" aria-hidden="true" />Chargement de la tendance…</p>;
  if (state.phase === "error") return <p className="text-xs text-red-700">{state.message}</p>;
  const series = trendSeries(state.data.points, state.data.unit);
  return (
    <div className="space-y-2 border-t border-slate-100 pt-2" data-testid={`kpi-${kpi.id}-trend`}>
      {series.map((s) => (
        <div key={s.key} className="h-32">
          {s.label && <p className="text-2xs text-slate-500">{s.label}</p>}
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={s.data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} width={40} tickFormatter={(v) => (s.currency ? String(Math.round(v / 100)) : v)} />
              <Tooltip formatter={(v) => (s.currency ? formatMinor(v, s.currency) : v)} labelFormatter={monthLabel} />
              <Line type="monotone" dataKey="value" stroke="#083346" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ))}
      <details className="text-xs text-slate-600">
        <summary className="cursor-pointer select-none">Voir les chiffres</summary>
        <table className="mt-1 w-full text-left" data-testid={`kpi-${kpi.id}-trend-table`}>
          <thead><tr><th className="font-medium">Mois</th>{series.map((s) => <th key={s.key} className="text-right font-medium">{s.label || "Valeur"}</th>)}</tr></thead>
          <tbody>
            {state.data.points.map((p, i) => (
              <tr key={p.month}><td>{monthLabel(p.month)}</td>{series.map((s) => <td key={s.key} className="text-right tabular-nums">{s.currency ? formatMinor(s.data[i].value, s.currency) : s.data[i].value}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

export function SectionCard({ section, kpiById, period, version, isMobile }) {
  return (
    <Card className="p-4 space-y-3" data-testid={`cockpit-section-${section.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-900">{section.label}</h2>
        {section.state === "partial" && <span className="text-xs text-amber-800">{STATE_LABELS.partial}</span>}
      </div>
      {section.state === "source_unavailable" ? (
        <p className="text-sm text-slate-600" data-testid={`cockpit-section-${section.id}-down`}>{STATE_LABELS.source_unavailable} : les autres domaines restent affichés. Réessayez dans un instant.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {section.kpis.map((entry) => <Tile key={entry.id} entry={entry} kpi={kpiById[entry.id]} period={period} version={version} isMobile={isMobile} />)}
        </div>
      )}
    </Card>
  );
}

const HIGHLIGHTS = ["rev.mrr", "rev.collected", "fin.expenses", "com.leads", "cus.live", "sup.created"];

// The headline figures of the company, with the shape of the last twelve months for the flows: the first thing a director reads.
export function Highlights({ sections, kpiById }) {
  const entries = HIGHLIGHTS.map((id) => {
    for (const section of sections) {
      const entry = section.kpis.find((k) => k.id === id);
      if (entry && entry.state !== "unavailable" && entry.state !== "no_data") return entry;
    }
    return null;
  }).filter(Boolean);
  if (!entries.length) return null;
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(10rem, 1fr))" }} data-testid="cockpit-highlights">
      {entries.map((entry) => <HighlightCard key={entry.id} entry={entry} kpi={kpiById[entry.id]} />)}
    </div>
  );
}

function HighlightCard({ entry, kpi }) {
  const trend = useTrend(entry.id, !!kpi && TREND_KPIS.includes(entry.id));
  if (!kpi) return null;
  const lines = entry.money ? moneyLines(entry.money) : null;
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-lg border border-slate-200 bg-white p-3" data-testid={`cockpit-highlight-${entry.id}`}>
      <p className="truncate text-xs font-medium text-slate-500">{kpi.label}</p>
      {lines ? lines.map((line) => <p key={line.currency} className="font-display text-lg font-bold leading-tight tabular-nums text-slate-900">{line.text}</p>)
        : <p className="font-display text-2xl font-bold leading-tight tabular-nums text-slate-900">{formatScalar(entry.value, kpi.unit)}</p>}
      <Delta entry={entry} kpi={kpi} compact />
      <div className="mt-auto"><TrendSpark kpi={kpi} trend={trend} /></div>
    </div>
  );
}

export function Exceptions({ items }) {
  if (!items.length) {
    return <Card className="p-4 text-sm text-slate-600" data-testid="cockpit-exceptions-empty">Rien d'urgent à regarder pour l'instant dans les domaines que vous voyez.</Card>;
  }
  return (
    <Card className="p-4 space-y-2" data-testid="cockpit-exceptions">
      <h2 className="text-base font-semibold text-slate-900">À regarder</h2>
      <ul className="divide-y divide-slate-100">
        {items.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`cockpit-exception-${e.id}`}>
            <div className="min-w-0">
              <p className="text-sm text-slate-900">{e.label}</p>
              <p className="text-xs text-slate-500">{e.domain}</p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <span className={`rounded-full px-2 py-0.5 text-2xs font-medium ${e.severity === "critical" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>{SEVERITY_LABELS[e.severity]}</span>
              {e.to && <Link to={e.to} className="inline-flex items-center gap-1 text-xs text-jamora hover:underline">Voir<ArrowRight size={12} aria-hidden="true" /></Link>}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
